import React, { Suspense, useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "./components/AuthProvider";
import { CartProvider } from "./components/CartProvider";
import { MainLayout } from "./components/MainLayout";
import { CookieConsent } from "./components/CookieConsent";
import "./lib/i18n";
import { useRTLDirection } from "./hooks/useRTLDirection";
import { initGA, logPageView } from "./lib/analytics";

// Loading component
function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>
  );
}

const HomePage = React.lazy(() =>
  import("./components/HomePage").then((module) => ({
    default: module.HomePage,
  })),
);
const ProductsPage = React.lazy(() =>
  import("./components/ProductsPage").then((module) => ({
    default: module.ProductsPage,
  })),
);
const ProductDetailPage = React.lazy(() =>
  import("./components/ProductDetailPage").then((module) => ({
    default: module.ProductDetailPage,
  })),
);
const Cart = React.lazy(() =>
  import("./components/Cart").then((module) => ({
    default: module.Cart,
  })),
);
const CheckoutPage = React.lazy(() =>
  import("./components/CheckoutPage").then((module) => ({
    default: module.CheckoutPage,
  })),
);
const AuthPage = React.lazy(() =>
  import("./components/AuthPage").then((module) => ({
    default: module.AuthPage,
  })),
);
const OrderSuccessPage = React.lazy(() =>
  import("./components/OrderSuccessPage").then((module) => ({
    default: module.OrderSuccessPage,
  })),
);
const AboutPage = React.lazy(() =>
  import("./components/AboutPage").then((module) => ({
    default: module.AboutPage,
  })),
);
const PrivacyPolicyPage = React.lazy(() =>
  import("./components/PrivacyPolicyPage").then((module) => ({
    default: module.PrivacyPolicyPage,
  })),
);
const TermsPage = React.lazy(() =>
  import("./components/TermsPage").then((module) => ({
    default: module.TermsPage,
  })),
);
const RefundPage = React.lazy(() =>
  import("./components/RefundPage").then((module) => ({
    default: module.RefundPage,
  })),
);
const DashboardPage = React.lazy(() =>
  import("./components/DashboardPage").then((module) => ({
    default: module.DashboardPage,
  })),
);
const DiscountsPage = React.lazy(() =>
  import("./components/DiscountsPage").then((module) => ({
    default: module.DiscountsPage,
  })),
);

// Cart wrapper component that has access to cart context
function CartWrapper() {
  return <Cart />;
}

// Language wrapper to sync URL with i18n
function LanguageWrapper() {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const supportedLanguages = ["en", "es", "he"]; // Add your supported languages

    // Normalize i18n language to 2-letter code (e.g., en-US -> en)
    const normalizedI18nLang = i18n.language.split("-")[0];

    // Default to "en" if the normalized language is not supported
    const defaultLang = supportedLanguages.includes(normalizedI18nLang)
      ? normalizedI18nLang
      : "en";

    // If URL has no language or invalid language, redirect to default language
    if (!lang || !supportedLanguages.includes(lang)) {
      // Use lookahead to only strip a 2-letter segment that is followed by / or end-of-string,
      // preventing accidental stripping of letters from longer path segments (e.g. /pages → /pa + ges)
      const pathWithoutLang = location.pathname.replace(
        /^\/[a-z]{2}(?=\/|$)/,
        "",
      );
      // Ensure the remaining path starts with / to avoid concatenation bugs like /heages/products
      const safePath =
        pathWithoutLang.startsWith("/") || pathWithoutLang === ""
          ? pathWithoutLang
          : `/${pathWithoutLang}`;
      navigate(`/${defaultLang}${safePath}${location.search}`, {
        replace: true,
      });
      return;
    }

    // If URL language differs from i18n language, sync i18n to URL
    if (
      lang &&
      supportedLanguages.includes(lang) &&
      normalizedI18nLang !== lang
    ) {
      i18n.changeLanguage(lang);
    }
  }, [lang, navigate, location.pathname, location.search, i18n]);

  return <MainLayout />;
}

function App() {
  const { i18n } = useTranslation();
  const location = useLocation();
  // Initialize RTL direction on app load
  useRTLDirection();

  // Check for existing cookie consent and initialize GA if accepted
  useEffect(() => {
    const analyticsEnabled = localStorage.getItem("analyticsEnabled");
    if (analyticsEnabled === "true") {
      initGA();
    }
  }, []);

  // Track page views with Google Analytics (only if consent given)
  useEffect(() => {
    const analyticsEnabled = localStorage.getItem("analyticsEnabled");
    if (analyticsEnabled === "true") {
      logPageView(location.pathname + location.search);
    }
  }, [location]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, location.search]);

  // Update HTML lang and dir attributes
  useEffect(() => {
    document.documentElement.lang = i18n.language;
    document.documentElement.dir = i18n.dir();
  }, [i18n.language]);

  // Normalize language to 2-letter code
  const supportedLanguages = ["en", "es", "he"];
  const normalizedLang = i18n.language.split("-")[0];
  const defaultLang = supportedLanguages.includes(normalizedLang)
    ? normalizedLang
    : "en";

  return (
    <AuthProvider>
      <CartProvider>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Redirect root to default language */}
            <Route
              path="/"
              element={<Navigate to={`/${defaultLang}`} replace />}
            />

            {/* Language-prefixed routes */}
            <Route path="/:lang" element={<LanguageWrapper />}>
              <Route index element={<HomePage />} />
              <Route path="products" element={<ProductsPage />} />
              <Route path="products/:id" element={<ProductDetailPage />} />
              <Route path="discounts" element={<DiscountsPage />} />
              <Route path="about" element={<AboutPage />} />
              <Route path="privacy" element={<PrivacyPolicyPage />} />
              <Route path="terms" element={<TermsPage />} />
              <Route path="refund" element={<RefundPage />} />
              <Route path="checkout" element={<CheckoutPage />} />
              <Route path="dashboard/*" element={<DashboardPage />} />
              <Route path="order-success" element={<OrderSuccessPage />} />
            </Route>

            {/* Standalone routes without MainLayout */}
            <Route path="/:lang/login" element={<AuthPage />} />
            <Route path="/:lang/register" element={<AuthPage />} />

            {/* Catch all redirect to home */}
            <Route
              path="*"
              element={<Navigate to={`/${defaultLang}`} replace />}
            />
          </Routes>
        </Suspense>

        {/* Cart sidebar - global */}
        <Suspense fallback={null}>
          <CartWrapper />
        </Suspense>

        {/* Toast notifications */}
        <Toaster
          position="top-center"
          expand={false}
          richColors
          closeButton
          dir={i18n.dir()}
        />

        {/* Cookie Consent Banner */}
        <CookieConsent />
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
